import BundleApi from "@yoco/shop-sdk/lib/api/BundleApi"
import CartApi from "@yoco/shop-sdk/lib/api/CartApi"
import OAuthApi from "@yoco/shop-sdk/lib/api/OAuthApi"
import ProductApi from "@yoco/shop-sdk/lib/api/ProductApi"
import HttpClient from "@yoco/shop-sdk/lib/HttpClient"
import { validate as validateUUID } from "uuid"

import { storyblokLinkToYocoLink } from "./storyblok"
import {
  getBundlePrices,
  getProductPrices,
  hasDiscountedPrice,
} from "./V2/consolidateItem"

import { Color } from "@constants/V2/color"
import { isPurchasableBundle, PurchasableItem } from "@hooks/V2/storyblok"

const http = new HttpClient(`${process.env.GATSBY_SHOP_API_URL}`)
export const shop = {
  carts: new CartApi(http),
  products: new ProductApi(http),
  bundles: new BundleApi(http),
  oauth: new OAuthApi(http),
}

const isBrowser = typeof window !== "undefined"

export const localStorageAvailable = (): boolean => {
  if (!isBrowser) {
    return false
  }
  try {
    localStorage.setItem("testCookie", "testCookie")
    localStorage.removeItem("testCookie")
    return true
  } catch (e) {
    return false
  }
}

export const sessionStorageAvailable = (): boolean => {
  if (!isBrowser) {
    return false
  }
  try {
    sessionStorage.setItem("testCookie", "testCookie")
    sessionStorage.removeItem("testCookie")
    return true
  } catch (e) {
    return false
  }
}

export const fetchCartId = (): string | null => {
  if (localStorageAvailable()) {
    return localStorage.getItem("cartId")
  }
  return null
}

export const storeCartId = (id: string): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("cartId", id)
  }
}

export const itemAlreadyInCart = (item: PurchasableItem): boolean => {
  return isPurchasableBundle(item)
    ? bundleAlreadyInCart(item.bundleCode)
    : productAlreadyInCart(item.sku)
}

export const productAlreadyInCart = (sku: string): boolean => {
  const products = fetchCartProducts()
  const inCart = products?.find((product) => {
    // This is a hotfix for users who already have existing cart items without IDs
    if (!product.id) {
      clearCart()
      return false
    }

    if (!isStoredProduct(product)) return false
    return product.sku === sku
  })

  return inCart ? true : false
}

export const bundleAlreadyInCart = (bundleCode: string): boolean => {
  const products = fetchCartProducts()

  const inCart = products?.find((product) => {
    // This is a hotfix for users who already have existing cart items without IDs
    if (!product.id) {
      clearCart()
      return false
    }

    if (!isStoredBundle(product)) return false

    return product.bundleCode === bundleCode
  })

  return inCart ? true : false
}

export interface StoredItemBase {
  id: string
  quantity: number
  storefront: string
}

export interface StoredProduct extends StoredItemBase {
  sku: string
  type: "product"
}

export interface StoredBundle extends StoredItemBase {
  bundleCode: string
  type: "bundle"
}

export type StoredItemType = "product" | "bundle"

export type StoredItem = StoredProduct | StoredBundle

export const isStoredProduct = (
  storedItem: StoredItem
): storedItem is StoredProduct => {
  return storedItem.type === "product"
}

export const isStoredBundle = (
  storedItem: StoredItem
): storedItem is StoredBundle => {
  return storedItem.type === "bundle"
}

export const fetchCartProducts = ():
  | (StoredBundle | StoredProduct)[]
  | null => {
  if (localStorageAvailable()) {
    const products = localStorage.getItem("cartProducts")
    return products ? JSON.parse(products) : null
  }

  return null
}

export const getProductName = (
  sdkProduct: YocoCom.SdkProduct | null,
  storyblokName: string
): string => {
  return sdkProduct?.short_name || storyblokName
}

export type { ProductPrice } from "./V2/consolidateItem"
export {
  getProductPrices,
  getBundlePrices,
  hasDiscountedPrice,
} from "./V2/consolidateItem"

export const consolidateProduct = (
  productId: string,
  quantity: number,
  sdkProduct: YocoCom.SdkProduct,
  storyblokProduct: Storyblok.ProductConfig
): YocoCom.ConsolidatedProduct => {
  const productPrices = getProductPrices(sdkProduct)

  const originalPrice = productPrices?.originalPrice
    ? Number.parseFloat(productPrices?.originalPrice)
    : undefined

  const savingsAmount =
    originalPrice && Number.parseFloat(productPrices.price)
      ? originalPrice - Number.parseFloat(productPrices.price)
      : 0

  return {
    id: productId,
    sku: storyblokProduct.sku ?? sdkProduct.sku,
    name: sdkProduct.short_name ?? storyblokProduct.name,
    cartTitle:
      storyblokProduct.overrideCartTitle ||
      sdkProduct.short_name ||
      storyblokProduct.name,
    cartPreviewImage: storyblokProduct.cartPreviewImage?.filename
      ? storyblokProduct.cartPreviewImage
      : sdkProduct.image,
    thumbnail: storyblokProduct.thumbnailImage?.filename
      ? storyblokProduct.thumbnailImage
      : sdkProduct.image,
    quantity: quantity,
    maxQuantity: sdkProduct.max_quantity_per_cart ?? undefined,
    price: productPrices.price,
    originalPrice: originalPrice?.toString(),
    savingsAmount: savingsAmount,
    detailLink:
      storyblokLinkToYocoLink(storyblokProduct.detailsLink)?.url ?? "",
    waitlistLink: storyblokProduct.waitlistLink
      ? storyblokLinkToYocoLink(storyblokProduct.waitlistLink)
      : undefined,
    description: sdkProduct.short_description || sdkProduct.description,
    galleryItems: storyblokProduct.galleryItems,
    thirdPartyReviews: sdkProduct.third_party_reviews,
    recommendedProducts: storyblokProduct.recommendedProducts,
    showRecommendedProducts: storyblokProduct.showRecommendedProducts,
    reasonNotAvailable: sdkProduct.reason_not_available,
    priceSideScript: storyblokProduct.priceSuffixLabel,
    priceSubScript: storyblokProduct.priceInfoText,
    storefront: storyblokProduct.storefront,
    onPromotion: hasDiscountedPrice(
      productPrices.price,
      productPrices.originalPrice
    ),
    type: "product",
  }
}

export const consolidateBundle = (
  bundleId: string,
  quantity: number,
  sdkBundle: YocoCom.SdkBundle,
  storyblokBundle: Storyblok.BundleConfig
): YocoCom.ConsolidatedBundle => {
  const bundlePrices = getBundlePrices(sdkBundle)

  const originalPrice = bundlePrices?.originalPrice
    ? Number.parseFloat(bundlePrices?.originalPrice)
    : undefined

  const savingsAmount =
    originalPrice && Number.parseFloat(bundlePrices.price)
      ? originalPrice - Number.parseFloat(bundlePrices.price)
      : 0

  return {
    id: bundleId,
    bundleCode: storyblokBundle.code ?? sdkBundle.bundle_code,
    name: sdkBundle.short_name ?? storyblokBundle.name,
    cartTitle:
      storyblokBundle.overrideCartTitle ||
      sdkBundle.short_name ||
      storyblokBundle.name,
    cartPreviewImage: storyblokBundle.cartPreviewImage?.filename
      ? storyblokBundle.cartPreviewImage
      : sdkBundle.image,
    thumbnail: storyblokBundle.thumbnailImage?.filename
      ? storyblokBundle.thumbnailImage
      : sdkBundle.image,
    quantity: quantity,
    maxQuantity: sdkBundle.max_quantity_per_cart ?? undefined,
    price: bundlePrices.price,
    originalPrice: originalPrice?.toString(),
    priceSideScript: storyblokBundle.priceSidescript,
    priceSubScript: storyblokBundle.priceSubscript,
    savingsAmount: savingsAmount,
    detailLink: storyblokLinkToYocoLink(storyblokBundle.shopPage)?.url ?? "",
    waitlistLink: storyblokBundle.waitlistLink
      ? storyblokLinkToYocoLink(storyblokBundle.waitlistLink)
      : undefined,
    description: sdkBundle.short_description || sdkBundle.description,
    galleryItems: storyblokBundle.galleryItems,
    recommendedProducts: storyblokBundle.recommendedProducts,
    showRecommendedProducts: storyblokBundle.showRecommendedProducts,
    reasonNotAvailable: sdkBundle.reason_not_available,
    storefront: storyblokBundle.storefront,
    onPromotion: hasDiscountedPrice(
      bundlePrices.price,
      bundlePrices.originalPrice
    ),
    type: "bundle",
    reviewSkus: storyblokBundle.reviewSkus?.split(",") || [],
  }
}

export const storeCartProducts = (products: StoredItemBase[]): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("cartProducts", JSON.stringify(products))
  }
}

export const fetchCartPromoCode = (): string | null => {
  if (localStorageAvailable()) {
    return localStorage.getItem("promoCode")
  }
  return null
}

export const storeCartPromoCode = (promoCode: string): void => {
  if (localStorageAvailable()) {
    localStorage.setItem("promoCode", promoCode)
  }
}

export const clearCartPromoCode = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("promoCode")
  }
}

export const clearCartId = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("cartId")
  }
}

export const clearCart = (): void => {
  if (localStorageAvailable()) {
    localStorage.removeItem("cartId")
    localStorage.removeItem("cartProducts")
    localStorage.removeItem("promoCode")
  }
}

export const humanizeAddressText = (
  address: YocoCom.ShippingAddress
): string => {
  const addressList = []

  if (address.country) {
    addressList.push(address.country)
  }
  if (address.street) {
    addressList.push(address.street)
  }
  if (address.suburb) {
    addressList.push(address.suburb)
  }
  if (address.city) {
    addressList.push(address.city)
  }
  if (address.province) {
    addressList.push(address.province)
  }
  if (address.postalCode) {
    addressList.push(address.postalCode)
  }

  return addressList.join(", ")
}

export const isShopEnabled = (): boolean => {
  // This fetches a static setting determined at build time

  if (process.env.GATSBY_IS_SHOP_ENABLED === "true") {
    return true
  } else {
    return false
  }
}

export const isConsolidatedProduct = (
  cartItem: YocoCom.ConsolidatedCartItem
): cartItem is YocoCom.ConsolidatedProduct => {
  return cartItem.type === "product"
}

export const isConsolidatedBundle = (
  cartItem: YocoCom.ConsolidatedCartItem
): cartItem is YocoCom.ConsolidatedBundle => {
  return cartItem.type === "bundle"
}

export const getProductSavingsBadge = (
  item: YocoCom.ConsolidatedPurchasableItem
): Storyblok.Badge | null => {
  const savings =
    item.savingsAmount ||
    (item.originalPrice && item.price
      ? Number.parseFloat(item.originalPrice) - Number.parseFloat(item.price)
      : 0)

  if (!savings || savings <= 0) return null

  const originalPriceNum = item.originalPrice
    ? Number.parseFloat(item.originalPrice)
    : null
  const percentageSaved =
    originalPriceNum && originalPriceNum > 0
      ? Math.round((savings / originalPriceNum) * 100)
      : null

  if (!percentageSaved) return null

  return {
    _uid: "savings-badge",
    component: "badge",
    text: `-${percentageSaved}%`,
    color: Color.Orange,
    textColor: Color.White,
  } as Storyblok.Badge
}

export const getSavingsBadge = (
  price: number,
  originalPrice?: number
): YocoCom.Badge | null => {
  if (!originalPrice || !price || originalPrice <= price) return null

  const percentageSaved = Math.round(
    ((originalPrice - price) / originalPrice) * 100
  )

  return {
    _uid: "savings-badge",
    component: "badge",
    text: `-${percentageSaved}%`,
    color: Color.Orange,
    textColor: Color.White,
  } as YocoCom.Badge
}

export const validateBusinessUUID = (businessUUID: string): boolean => {
  // Business UUIDs are made up of a timestamp in miliseconds and a uuid4 {timestamp}-{uuid4}

  if (!businessUUID) return false

  const parts = businessUUID.split("-")
  const timestamp = parts.shift()
  const uuid = parts.join("-")
  const timestampRegex = /^\d{13}$/

  return timestampRegex.test(timestamp || "") && validateUUID(uuid)
}
